import React, { useState, useContext } from 'react';
import { Modal, Input, Button, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AppContext } from 'context/AppContext';
import { createTeam } from 'services/TeamService';
import { Campaign } from 'models/CommonType';
import './TeamCreationModal.css';

interface TeamCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: Campaign;
  authKey: string;
  onSuccess: () => void;
  isUpdating?: boolean;
  currentTeamName?: string;
}

const TeamCreationModal: React.FC<TeamCreationModalProps> = ({
  isOpen,
  onClose,
  campaign,
  authKey,
  onSuccess,
  isUpdating = false,
  currentTeamName = ''
}) => {
  const { t } = useTranslation();
  const { commonData } = useContext(AppContext);
  const [teamName, setTeamName] = useState(currentTeamName);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setTeamName('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!teamName.trim()) {
      message.error('Please enter a team name');
      return;
    }

    if (!commonData.userDetails?.id) {
      message.error('User information not available');
      return;
    }

    // Construct URLs from IDs (following REST API pattern)
    const userUrl = `${process.env.REACT_APP_DOMAIN}/api/users/${commonData.userDetails.id}/`;
    const campaignUrl = `${process.env.REACT_APP_DOMAIN}/api/campaigns/${campaign.id}/`;

    setIsLoading(true);
    
    try {
      if (isUpdating) {
        // TODO: Implement team update API when needed
        message.success('Team updated successfully');
      } else {
        await createTeam(
          teamName.trim(),
          campaignUrl,
          userUrl,
          authKey
        );
        message.success('Team created successfully');
      }
      
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Team creation/update error:', error);
      message.error(error.message || 'Failed to create team');
    } finally {
      setIsLoading(false);
    }
  };

  const title = isUpdating 
    ? t('#Teams.update_team_title')
    : t('#Teams.create_team_title');

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      centered
      className="team-creation-modal"
      width={400}
      closable={false}
    >
      <div className="team-modal-content">
        {/* Header */}
        <div className="team-modal-header">
          <h2 className="team-modal-title">{title}</h2>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={handleClose}
            className="team-modal-close"
            disabled={isLoading}
          />
        </div>

        {/* Team Name Input */}
        <div className="team-modal-body">
          <div className="team-input-container">
            <Input
              placeholder={t('#Teams.team_name_placeholder')}
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              onPressEnter={handleSubmit}
              size="large"
              maxLength={50}
              disabled={isLoading}
              className="team-name-input"
              autoFocus
            />
          </div>

          {/* Action Button */}
          <div className="team-modal-actions">
            <Button
              type="primary"
              size="large"
              onClick={handleSubmit}
              loading={isLoading}
              disabled={!teamName.trim()}
              className="team-submit-button"
              block
            >
              {isLoading 
                ? (isUpdating ? t('#Teams.updating_team') : t('#Teams.creating_team'))
                : (isUpdating ? t('#Common.update') : t('#Common.create'))
              }
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TeamCreationModal;